import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { AbstractMedia } from 'types/AbstractMedia';
import { MediaEntityType, MediaSubType } from 'types/MediaTypeEnum';

import useUpload from 'hooks/useUpload';
import { useAddMessageMutation } from 'modules/chat/service';

import { MediaSupport } from 'types/mediaTypes';
import { GET_MEDIA_UPLOAD_URL } from 'constants/url';

import { isPublicDevice } from 'modules/dealers/selectors';
import { getChatMessageFileLimit, getChatMessageMediaSupport } from 'modules/refData/selectors';
import { getUnreadCountText, haveUnreadMessages as haveUnreadMessagesSelector } from 'modules/chat/selectors';

import Modal, { ModalProps } from 'components/ui/Modal';
import { FileList, Textarea, Icon } from 'components/ui';
import Button, { ButtonType } from 'components/ui/Button';

import classNames from 'classnames';
import MessageList from './MessageList';

type ChatModalProps = Pick<ModalProps, 'open' | 'onClose'> & {
  selfServiceId: string;
  maxMessageLength?: number;
};

const ChatModal: React.FC<ChatModalProps> = ({
  open, onClose, selfServiceId, maxMessageLength,
}) => {
  const intl = useIntl();

  const [message, setMessage] = React.useState('');

  const isPublic = useSelector(isPublicDevice);
  const unreadCount = useSelector(getUnreadCountText);
  const fileLimit = useSelector(getChatMessageFileLimit);
  const supportedTypes = useSelector(getChatMessageMediaSupport);
  const hasUnreadMessages = useSelector(haveUnreadMessagesSelector);

  const {
    upload, isLoading: isUploading, remove, isDeleting, browse, medias, clearMedias,
  } = useUpload({ selfServiceId, limit: fileLimit });
  const [addMessage, { isLoading: isAddingMessage }] = useAddMessageMutation();

  const disableUpload = medias.length >= fileLimit;
  const isLoading = isUploading || isDeleting || isAddingMessage;

  // The send button is disabled when `isLoading` is `true`, or there is no message, or there is no media.
  const disabledButton = React.useMemo(() => {
    let disabledState = isLoading;

    if (!disabledState) {
      // If there is 0 media, the button is disabled
      // Otherwise, the button is disabled when the (trimmed) message is empty
      if (!medias || medias.length === 0) {
        disabledState = message?.trim().length === 0;
      }
    }

    return disabledState;
  }, [isLoading, medias, message]);

  const handleChangeMessage = React.useCallback(
    ({ target }: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(target.value),
    [],
  );

  const handleAddMessage = React.useCallback(async () => {
    try {
      await addMessage({ selfServiceId, message, medias }).unwrap();
      setMessage('');
      clearMedias();
    } catch {
      // Notif?
    }
  }, [addMessage, selfServiceId, message, medias, clearMedias]);

  const handleUpload = React.useCallback(async () => {
    try {
      const files = Array.from(await browse({ multiple: true, accept: supportedTypes as MediaSupport }));

      await Promise.all(files.map((file) => upload({
        file,
        filename: file.name,
        contentType: file.type,
        url: GET_MEDIA_UPLOAD_URL,
        entityType: MediaEntityType.MESSAGE,
        subType: MediaSubType.MEDIAS,
      })));
    } catch { /* Do nothing */ }
  }, [browse, upload, supportedTypes]);

  const handleOnDelete = React.useCallback(({ uploadId }: AbstractMedia) => remove(uploadId), [remove]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="modal-large"
      title={(
        <div className="flex justify-center mb-4">
          <h2 className="relative flex gap-2 w-fit justify-center items-center">
            <FormattedMessage id="page.chat.modal.title" defaultMessage="Messages" />
            {hasUnreadMessages && (
              <div className="h-7 rounded-full p-1.5 bg-brand-default ">
                <span className="min-w-4 flex leading-4 items-center text-xs text-white justify-center">
                  {unreadCount}
                </span>
              </div>
            )}
          </h2>
        </div>
      )}
    >
      <hr className="border-gray-200" aria-hidden="true" />
      <div className="flex flex-col max-h-1/2-screen">
        <MessageList selfServiceId={selfServiceId} />
        <hr className="border-gray-200" aria-hidden="true" />
        {medias?.length > 0 && (
          <FileList
            hideFilename
            medias={medias}
            onDelete={!isAddingMessage && handleOnDelete}
            className="h-32 [&>div]:flex-initial! [&>div]:shrink-0! [&>div>div]:bg-neutral-50! [&>div]:size-16! flex-nowrap overflow-x-auto pt-m"
          />
        )}
        <div
          className="flex items-center justify-between w-full pt-s"
        >
          <div className="overflow-hidden grow rounded-chat border border-gray-200 flex gap-2xs pl-xs items-center mr-s">
            {!isPublic && (
              <Button
                testId="upload-button"
                onClick={handleUpload}
                type={ButtonType.LIGHT}
                disabled={disableUpload}
                className={classNames('p-0! size-5', disableUpload && 'text-border-default')}
                aria-label={intl.formatMessage({ id: 'accessibility.button.upload', defaultMessage: 'Upload a new media' })}
              >
                <Icon className="fb-icon--medias text-xl leading-none" />
              </Button>
            )}
            <Textarea
              rows={2}
              value={message}
              className="grow"
              data-testid="message-input"
              maxLength={maxMessageLength}
              onChange={handleChangeMessage}
              inputClassName="m-0 py-2 resize-none font-normal text-black! content-center border-none"
              placeholder={intl.formatMessage({ id: 'page.chat.modal.placeholder', defaultMessage: 'Your message' })}
            />
          </div>
          <Button
            loading={isLoading}
            testId="message-send"
            disabled={disabledButton}
            onClick={handleAddMessage}
            className="size-14 bg-brand-default shadow-md p-3! flex items-center justify-center"
            aria-label={intl.formatMessage({ id: 'accessibility.sendMessage', defaultMessage: 'Send a new message' })}
          >
            <Icon className="fb-icon--send text-3xl kiosk:text-4xl text-white"/>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChatModal;
